import React, { Component } from 'react';
import PropTypes from 'prop-types';
//Opentok
import { OTSubscriber } from 'opentok-react';
import { Doctor, Patient, toDisplayName } from '../../../../utils/common/NamingTerms';
import ScreenShotEditModal from './ScreenShotEditModal';
import { SubscriberHosts, SubscriberSlot } from './SubscriberSlot';
import { emitLiveCaption, emitStreamId } from '../../../action/SocketActions';

let width = window.innerWidth;
let isMobileView = false;
if (width <= 768) {
    isMobileView = true;
}
if (!isMobileView) {
    width = (width / 100) * 65;
}

// const subscriberOptionsSmallVideo = {
//     insertMode: 'append',
//     showControls: true,
//     height: 150,
//     width: 160,
// };

class ClassRoomSubscribers extends Component {
    static propTypes = {
        streams: PropTypes.array,
        sessionHelper: PropTypes.any,
    };
    constructor(props) {
        super(props);
        this.state = {
            subscriber: null,
            screenShare: false,
            currentIndex: 0,
            showStudents: false,
            showWhiteboard: PropTypes.bool,
            caption: '',
            captions: [],
            raisedHands: new Set(),
        };
        this.maxScrollWidth = 0;
        this.carousel = React.createRef();

        this.subscribers = new Map();

        // One persistent host element per stream - see SubscriberSlot.
        this.subscriberHosts = new SubscriberHosts();
        // Streams placed by the current render.
        this.placedStreams = new Map();

        // Track per-stream caption timeouts to prevent emission leaks
        this.captionTimeouts = new Map();

        // Track last emitted caption per stream to prevent duplicate emissions
        this.lastEmittedCaptions = new Map();

        // Track retry timeout for unmatched streams
        this.retryEmitTimeout = null;
        this.streamIdReemitTimeouts = [];
        this.streamIdMatchRetryCount = 0;

        this.subscriberEventHandlers = {
            captionReceived: (event) => {
                const { studentDetails, streams } = this.props;
                const urlParams = new URLSearchParams(window.location.search);

                //stream that matches event.streamId
                const matchedStream = streams.find(
                    (stream) => (stream.streamId || stream.id) === event.streamId
                );

                // PRODUCTION FIX: Try multiple strategies to resolve userId for captions
                let resolvedUserId = null;

                // Strategy 1: Use streamId mapping (most reliable after reconnect/rejoin)
                try {
                    const storedMappings = JSON.parse(
                        sessionStorage.getItem('streamIdMappings') || '{}'
                    );
                    const mappedUserId = storedMappings[event.streamId];
                    if (this.isValidRemoteUserId(mappedUserId)) {
                        resolvedUserId = mappedUserId;
                    }
                } catch (e) {
                    console.error('ClassRoomSubscribers: Error reading streamIdMappings:', e);
                }

                // Strategy 2: Resolve from stream metadata/name (works when identity changes slightly)
                if (!resolvedUserId) {
                    const matchedUser = this.resolveUserForStream(matchedStream);
                    if (matchedUser?.userId) {
                        resolvedUserId = matchedUser.userId;
                    }
                }

                // Strategy 3: Use identity map (handles rejoin with identity change)
                if (!resolvedUserId && matchedStream?.name) {
                    try {
                        const identityMapRaw = sessionStorage.getItem('userIdentityMap');
                        if (identityMapRaw) {
                            const identityMap = JSON.parse(identityMapRaw);
                            const mappedUserId = identityMap[`name:${matchedStream.name}`];
                            if (this.isValidRemoteUserId(mappedUserId)) {
                                resolvedUserId = mappedUserId;
                            }
                        }
                    } catch (e) {
                        console.error('ClassRoomSubscribers: Error reading identity map:', e);
                    }
                }

                // Strategy 4: Fallback to resolveTargetUserId (1-to-1 sessions only)
                if (!resolvedUserId) {
                    resolvedUserId = this.resolveTargetUserId();
                }

                const userInfo = {
                    caption: event.caption,
                    sessionId:
                        this.props.visitId ||
                        this.props.studentDetails?.id ||
                        urlParams.get('visitId'),
                    streamId: event.streamId,
                    isFinal: event.isFinal !== false,
                    userId: resolvedUserId,
                };

                // Only emit if we have a valid userId
                if (!userInfo.userId) {
                    console.warn(
                        'ClassRoomSubscribers: Skipping caption emit - no valid userId found for stream:',
                        {
                            streamId: event.streamId,
                            streamName: matchedStream?.name,
                            availableUsers: {
                                sessionUsers: studentDetails?.sessionUsers?.map((u) => u.name),
                                externalUsers: studentDetails?.externalUsersDetails?.map(
                                    (u) => u.name
                                ),
                            },
                            triedStrategies: [
                                'streamId_mapping',
                                'name_match',
                                'identity_map',
                                'target_resolution',
                            ],
                        }
                    );
                    return;
                }
                // Deduplicate: skip if this exact caption was already emitted for this stream
                const lastCaption = this.lastEmittedCaptions.get(event.streamId);
                if (lastCaption === event.caption && event.isFinal) {
                    return;
                }

                this.setState({ caption: event.caption });
                // Note: streamId mappings are emitted in componentDidUpdate, not here

                // Clear existing timeout for this stream to prevent multiple emissions
                if (this.captionTimeouts.has(event.streamId)) {
                    clearTimeout(this.captionTimeouts.get(event.streamId));
                }

                // Set new timeout and store reference
                const timeoutId = setTimeout(() => {
                    emitLiveCaption(userInfo);
                    // Track this caption as emitted
                    this.lastEmittedCaptions.set(event.streamId, event.caption);
                    // Clean up timeout reference
                    this.captionTimeouts.delete(event.streamId);
                }, 2000);

                this.captionTimeouts.set(event.streamId, timeoutId);
            },
            videoEnabled: (videoEnabled) => {},
            audioEnabled: (audioEnabled) => {},
        };
    }

    componentDidMount() {
        const { sessionHelper } = this.props;

        sessionHelper.session.on('signal:raiseHand', (event) => {
            const { data } = event;

            const { raiseHand, streamId } = JSON.parse(data);
            if (raiseHand) {
                this.setState((prevState) => ({
                    raisedHands: new Set(prevState.raisedHands).add(streamId),
                }));
            } else {
                this.setState((prevState) => {
                    const newRaisedHands = new Set(prevState.raisedHands);
                    newRaisedHands.delete(streamId);
                    return { raisedHands: newRaisedHands };
                });
            }
        });

        // NEW: Listen for requests to re-emit streamId mappings for all remote streams.
        // This complements the Publisher's listener - when someone (re)joins and requests
        // mappings, we re-process all remote streams we're currently receiving.
        sessionHelper.session.on('signal:requestStreamIds', (event) => {
            const currentConnectionId = sessionHelper?.session?.connection?.connectionId;
            if (
                event.from?.connectionId &&
                currentConnectionId &&
                event.from.connectionId === currentConnectionId
            ) {
                return; // Ignore our own request
            }
            this.scheduleStreamIdReEmission();
        });

        sessionHelper.session.on('signal:streamIdMapping', (event) => {
            const currentConnectionId = sessionHelper?.session?.connection?.connectionId;
            if (
                event.from?.connectionId &&
                currentConnectionId &&
                event.from.connectionId === currentConnectionId
            ) {
                return;
            }

            try {
                const mapping = JSON.parse(event.data || '{}');
                if (mapping?.streamId && this.isValidRemoteUserId(mapping.userId)) {
                    const storedMappings = JSON.parse(
                        sessionStorage.getItem('streamIdMappings') || '{}'
                    );
                    storedMappings[mapping.streamId] = mapping.userId;
                    sessionStorage.setItem('streamIdMappings', JSON.stringify(storedMappings));
                }
            } catch (e) {
                console.error('[ClassRoomSubscribers] Error reading streamIdMapping signal:', e);
            }
        });
    }

    componentWillUnmount() {
        // Clear all pending caption timeouts to prevent memory leaks
        this.captionTimeouts.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.captionTimeouts.clear();
        this.lastEmittedCaptions.clear();

        // Clear retry timeout if pending
        if (this.retryEmitTimeout) {
            clearTimeout(this.retryEmitTimeout);
            this.retryEmitTimeout = null;
        }
        this.streamIdReemitTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
        this.streamIdReemitTimeouts = [];

        this.subscriberHosts.clear();

        // Clean up subscribers
        this.subscribers.forEach((sub) => {
            try {
                sub.destroy();
            } catch (e) {
                console.warn('Error destroying subscriber:', e);
            }
        });
        this.subscribers.clear();
    }

    getCurrentUserId = () => {
        const storedSession = JSON.parse(sessionStorage.getItem('userSession') || 'null');
        const urlParams = new URLSearchParams(window.location.search);

        return urlParams.get('userId') || storedSession?.id || this.props.userId || null;
    };

    isGroupLikeSession = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionType = (urlParams.get('sessionType') || '').toLowerCase();

        return !!(
            this.props.sessionTypeParamsGroup ||
            this.props.isConferenceCall ||
            this.props.OwnerAsCollaborator ||
            sessionType.includes('group') ||
            sessionType.includes('class') ||
            sessionType.includes('conference')
        );
    };

    resolveTargetUserId = () => {
        if (this.isGroupLikeSession()) {
            return null;
        }

        const myUserId = this.getCurrentUserId();
        // const urlParams = new URLSearchParams(window.location.search);

        // ONLY use sessionUsers/externalUsersDetails as source of truth
        // Do NOT fall back to stale props (callpatientUserId, calldoctorUserId, createdByUserId)
        const studentDetails = this.props.studentDetails;

        // Count total users in session (excluding self)
        const otherSessionUsers =
            studentDetails?.sessionUsers?.filter((u) => u.userId !== myUserId) || [];
        const otherExternalUsers =
            studentDetails?.externalUsersDetails?.filter((u) => u.userId !== myUserId) || [];
        const totalOtherUsers = otherSessionUsers.length + otherExternalUsers.length;

        // If 2+ other users (3+ total), we CAN'T guess which one is the target
        // This prevents returning wrong userId in multi-user sessions
        if (totalOtherUsers >= 2) {
            console.warn(
                'ClassRoomSubscribers: Cannot resolve target userId in multi-user session (3+ users):',
                {
                    totalUsers: totalOtherUsers + 1,
                    sessionUsers: otherSessionUsers.length,
                    externalUsers: otherExternalUsers.length,
                }
            );
            return null;
        }

        // If exactly 1 other user, return their userId (1-to-1 session)
        if (totalOtherUsers === 1) {
            const targetUser = otherSessionUsers[0] || otherExternalUsers[0];
            return targetUser?.userId || null;
        }

        // Return null if no valid target found
        console.warn('ClassRoomSubscribers: Could not resolve target userId from session data');
        return null;
    };

    isValidUserId = (userId) => userId && userId !== 'undefined' && userId !== 'null';

    isValidRemoteUserId = (userId) =>
        this.isValidUserId(userId) && userId !== this.getCurrentUserId();

    normalizeUserName = (name) =>
        (name || '')
            .toLowerCase()
            .replace(/\b(guest|anonymous)\b/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();

    getSessionUsers = () => [
        ...(this.props.studentDetails?.sessionUsers || []),
        ...(this.props.studentDetails?.externalUsersDetails || []),
    ];

    parseStreamConnectionData = (stream) => {
        const rawData = stream?.connection?.data;

        if (!rawData) {
            return {};
        }

        if (typeof rawData === 'object') {
            return rawData;
        }

        if (typeof rawData !== 'string') {
            return {};
        }

        try {
            return JSON.parse(rawData);
        } catch (e) {
            const parsedData = {};
            rawData.split(/[&;,|]/).forEach((part) => {
                const [key, ...valueParts] = part.split('=');
                const value = valueParts.join('=');
                if (key && value) {
                    parsedData[key.trim()] = decodeURIComponent(value.trim());
                }
            });
            return parsedData;
        }
    };

    resolveUserForStream = (stream) => {
        const allUsers = this.getSessionUsers();
        const myUserId = this.getCurrentUserId();
        const connectionData = this.parseStreamConnectionData(stream);
        const streamUserId =
            stream?.userId ||
            connectionData.userId ||
            connectionData.user_id ||
            connectionData.userID ||
            connectionData.id;

        if (this.isValidUserId(streamUserId) && streamUserId !== myUserId) {
            const matchingUser = allUsers.find((user) => user.userId === streamUserId);
            return matchingUser || { userId: streamUserId, name: stream?.name };
        }

        const candidateNames = [stream?.name, connectionData.name, connectionData.userName].filter(
            Boolean
        );

        for (const candidateName of candidateNames) {
            const exactMatch = allUsers.find(
                (user) => user.name === candidateName && this.isValidRemoteUserId(user.userId)
            );
            if (exactMatch?.userId) {
                return exactMatch;
            }

            const normalizedCandidate = this.normalizeUserName(candidateName);
            if (!normalizedCandidate) {
                continue;
            }

            const normalizedMatches = allUsers.filter((user) => {
                const normalizedUserName = this.normalizeUserName(user.name);
                return (
                    this.isValidRemoteUserId(user.userId) &&
                    normalizedUserName &&
                    (normalizedUserName === normalizedCandidate ||
                        normalizedUserName.includes(normalizedCandidate) ||
                        normalizedCandidate.includes(normalizedUserName))
                );
            });

            if (normalizedMatches.length === 1) {
                return normalizedMatches[0];
            }
        }

        return null;
    };

    logStreamAndUserId = (event) => {
        const { streams, studentDetails } = this.props;
        const myUserId = this.getCurrentUserId();

        if (!streams || streams.length === 0) {
            return; // Wait for proper data
        }

        let unmatchedStreams = [];

        streams.forEach((stream) => {
            const matchedUser = this.resolveUserForStream(stream);

            // PRODUCTION FIX: Multi-layer fallback for robust stream matching
            let userId = matchedUser?.userId;

            if (!userId || userId === 'undefined') {
                // Layer 1: Try sessionStorage cache (for re-emitted streams)
                try {
                    const storedMappings = JSON.parse(
                        sessionStorage.getItem('streamIdMappings') || '{}'
                    );
                    const storedUserId = storedMappings[stream.id];

                    if (this.isValidRemoteUserId(storedUserId)) {
                        userId = storedUserId;
                    }
                } catch (e) {
                    console.error(
                        '[ClassRoomSubscribers] Error reading streamIdMappings from sessionStorage:',
                        e
                    );
                }
            }

            // Layer 2: Check previous session data (handles name changes on rejoin)
            if (!userId || userId === 'undefined') {
                try {
                    const previousSessionData = JSON.parse(
                        sessionStorage.getItem('previousSessionData') || '{}'
                    );
                    const allPreviousUsers = [
                        ...(previousSessionData?.sessionUsers || []),
                        ...(previousSessionData?.externalUsersDetails || []),
                    ];

                    // Find user in previous data with matching name
                    const previousUser = allPreviousUsers.find((u) => u.name === stream.name);

                    if (this.isValidRemoteUserId(previousUser?.userId)) {
                        // Check if this userId exists in current sessionUsers (might be under different name)
                        const currentUser = [
                            ...(studentDetails?.sessionUsers || []),
                            ...(studentDetails?.externalUsersDetails || []),
                        ].find((u) => u.userId === previousUser.userId && u.userId !== myUserId);

                        if (currentUser) {
                            userId = currentUser.userId;
                        }
                    }
                } catch (e) {
                    console.error(
                        '[ClassRoomSubscribers] Error reading previousSessionData from sessionStorage:',
                        e
                    );
                }
            }

            // Layer 2.5: Use persistent identity map (handles multiple rejoins with identity changes)
            if (!userId || userId === 'undefined') {
                try {
                    const identityMapRaw = sessionStorage.getItem('userIdentityMap');
                    if (identityMapRaw) {
                        const identityMap = JSON.parse(identityMapRaw);

                        // Try to find userId by stream name
                        const mappedUserId = identityMap[`name:${stream.name}`];

                        if (this.isValidRemoteUserId(mappedUserId)) {
                            // Verify this userId exists in current session
                            const currentUser = [
                                ...(studentDetails?.sessionUsers || []),
                                ...(studentDetails?.externalUsersDetails || []),
                            ].find((u) => u.userId === mappedUserId && u.userId !== myUserId);

                            if (currentUser) {
                                userId = mappedUserId;
                            }
                        }
                    }
                } catch (e) {
                    console.error('[ClassRoomSubscribers] Error reading identity map:', e);
                }
            }

            // Layer 3: Smart guess for single unmatched stream + single new user
            if ((!userId || userId === 'undefined') && !this.isGroupLikeSession()) {
                const allCurrentUsers = [
                    ...(studentDetails?.sessionUsers || []),
                    ...(studentDetails?.externalUsersDetails || []),
                ];

                // Get list of users we've already matched
                const alreadyMatchedUserIds = new Set();
                streams.forEach((s) => {
                    const matched = allCurrentUsers.find((u) => u.name === s.name);
                    if (matched?.userId) alreadyMatchedUserIds.add(matched.userId);
                });

                // Find users not yet matched
                const unmatchedUsers = allCurrentUsers.filter(
                    (u) =>
                        this.isValidRemoteUserId(u.userId) && !alreadyMatchedUserIds.has(u.userId)
                );

                // If there's exactly 1 unmatched stream and 1 unmatched user, pair them
                if (
                    unmatchedUsers.length === 1 &&
                    streams.filter((s) => {
                        const m = allCurrentUsers.find((u) => u.name === s.name);
                        return !m || !m.userId || m.userId === 'undefined';
                    }).length === 1
                ) {
                    userId = unmatchedUsers[0].userId;
                }
            }

            // Only emit if we have a valid userId (either from matching or fallback)
            if (this.isValidRemoteUserId(userId)) {
                // Always use stream.id from the loop, never event.streamId
                // Using event.streamId would emit the same streamId for all users (bug!)
                const data = { streamId: stream.id, userId: userId };
                emitStreamId(data);

                // Update identity map with this successful match
                try {
                    const identityMapRaw = sessionStorage.getItem('userIdentityMap') || '{}';
                    const identityMap = JSON.parse(identityMapRaw);

                    // Initialize userId entry if not exists
                    if (!identityMap[userId]) {
                        identityMap[userId] = { names: [], userId: userId };
                    }

                    // Add stream name to this user's names (if not already present)
                    if (!identityMap[userId].names.includes(stream.name)) {
                        identityMap[userId].names.push(stream.name);
                    }

                    // Add reverse lookup
                    identityMap[`name:${stream.name}`] = userId;

                    sessionStorage.setItem('userIdentityMap', JSON.stringify(identityMap));
                } catch (e) {
                    console.error('[ClassRoomSubscribers] Error updating identity map:', e);
                }
            } else {
                unmatchedStreams.push({ streamId: stream.id, streamName: stream.name });
                console.warn(
                    '[ClassRoomSubscribers] ❌ STREAM NOT MATCHED - No fallback available:',
                    {
                        streamId: stream.id,
                        streamName: stream.name,
                        matchedUser: matchedUser ? 'found_but_no_userId' : 'not_found',
                        availableUsers: {
                            sessionUsers: studentDetails?.sessionUsers?.map(
                                (u) => `${u.name} (${u.userId || 'undefined'})`
                            ),
                            externalUsers: studentDetails?.externalUsersDetails?.map(
                                (u) => `${u.name} (${u.userId || 'undefined'})`
                            ),
                        },
                        cachedMappings: (() => {
                            try {
                                return JSON.parse(
                                    sessionStorage.getItem('streamIdMappings') || '{}'
                                );
                            } catch {
                                return null;
                            }
                        })(),
                    }
                );
            }
        });
        // Track unmatched streams in sessionStorage for pendingStreamReEmission logic
        if (unmatchedStreams.length > 0) {
            sessionStorage.setItem('hasUnmatchedStreams', 'true');
        } else {
            // Clear flag when all streams matched
            sessionStorage.removeItem('hasUnmatchedStreams');
            this.streamIdMatchRetryCount = 0;
        }

        // RETRY MECHANISM: If some streams couldn't be matched (user details not ready yet),
        // retry after a short delay to allow studentDetails to update
        if (unmatchedStreams.length > 0 && this.streamIdMatchRetryCount < 5) {
            // Clear any existing retry timeout
            if (this.retryEmitTimeout) {
                clearTimeout(this.retryEmitTimeout);
            }
            this.streamIdMatchRetryCount += 1;
            this.retryEmitTimeout = setTimeout(() => {
                this.logStreamAndUserId();
                this.retryEmitTimeout = null;
            }, 300);
        }
    };

    scheduleStreamIdReEmission = () => {
        this.streamIdReemitTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
        this.streamIdReemitTimeouts = [];
        this.logStreamAndUserId();
        [300, 1000, 3000].forEach((delay) => {
            const timeoutId = setTimeout(() => {
                this.logStreamAndUserId();
            }, delay);
            this.streamIdReemitTimeouts.push(timeoutId);
        });
    };

    // Centralized subscriber handler
    handleGetSubscriber = (stream, sub) => {
        this.subscribers.set(stream.id, sub);
        sub.subscribeToCaptions(true).catch((err) => {
            console.warn('Caption subscription failed for stream:', stream.id, err);
        });
        this.scheduleStreamIdReEmission();
    };

    // movePrev
    movePrev = () => {
        if (this.state.currentIndex > 0) {
            this.setState((prevState) => ({
                currentIndex: prevState.currentIndex - 1,
            }));
        }
    };

    // moveNext
    moveNext = () => {
        if (
            this.carousel.current !== null &&
            this.carousel.current.offsetWidth * this.state.currentIndex <= this.maxScrollWidth
        ) {
            this.setState((prevState) => ({
                currentIndex: prevState.currentIndex + 1,
            }));
        }
    };

    // shouldShowButtons
    shouldShowButtons = () => {
        if (this.carousel.current !== null) {
            return this.carousel.current.scrollWidth > this.carousel.current.offsetWidth;
        }
        return false;
    };

    // shouldShowLeftIcon
    shouldShowLeftIcon() {
        if (this.carousel.current !== null) {
            return this.carousel.current.scrollLeft > 0;
        }
        return false;
    }

    // shouldShowRightIcon
    shouldShowRightIcon() {
        if (this.carousel.current !== null) {
            const container = this.carousel.current;
            const maxScrollLeft = container.scrollWidth - container.clientWidth;
            return container.scrollLeft < maxScrollLeft;
        }
        return false;
    }

    // componentDidUpdate
    componentDidUpdate(prevProps) {
        const streamsChanged = this.props.streams !== prevProps.streams;
        const streamsLengthChanged = this.props.streams?.length !== prevProps.streams?.length;

        const currentSessionUsers = this.props.studentDetails?.sessionUsers;
        const prevSessionUsers = prevProps.studentDetails?.sessionUsers;
        const sessionUsersBecameAvailable =
            !prevSessionUsers && currentSessionUsers && this.props.streams?.length;

        // Detect sessionUsers content changes (user added/removed/rejoined)
        const sessionUsersContentChanged =
            prevSessionUsers &&
            currentSessionUsers &&
            JSON.stringify(prevSessionUsers.map((u) => u.userId).sort()) !==
                JSON.stringify(currentSessionUsers.map((u) => u.userId).sort());

        // Detect externalUsersDetails content changes
        const currentExternalUsers = this.props.studentDetails?.externalUsersDetails;
        const prevExternalUsers = prevProps.studentDetails?.externalUsersDetails;
        const externalUsersContentChanged =
            JSON.stringify(prevExternalUsers?.map((u) => u.userId).sort()) !==
            JSON.stringify(currentExternalUsers?.map((u) => u.userId).sort());

        // Emit streamIds if streams or users changed
        const shouldEmitStreamIds =
            streamsChanged ||
            streamsLengthChanged ||
            sessionUsersContentChanged ||
            externalUsersContentChanged ||
            sessionUsersBecameAvailable;

        if (shouldEmitStreamIds) {
            this.scheduleStreamIdReEmission();
        }

        // Case 1: streams list changed - destroy removed streams
        if (streamsChanged || streamsLengthChanged) {
            const activeStreamIds = new Set((this.props.streams || []).map((stream) => stream.id));
            this.subscriberHosts.prune(activeStreamIds);

            this.subscribers.forEach((sub, streamId) => {
                if (!activeStreamIds.has(streamId)) {
                    try {
                        sub.destroy();
                    } catch (e) {
                        console.warn('Error destroying removed subscriber during update:', e);
                    }
                    this.subscribers.delete(streamId);
                }
            });
        }

        if (this.carousel.current !== null) {
            this.carousel.current.scrollLeft =
                this.carousel.current.offsetWidth * this.state.currentIndex;
            this.maxScrollWidth =
                this.carousel.current.scrollWidth - this.carousel.current.offsetWidth;
        }
    }

    // onScreenShot - Use subscriber map
    onScreenShot(streamId) {
        const subscriber = this.subscribers.get(streamId);
        if (
            subscriber !== null &&
            typeof subscriber !== 'undefined' &&
            typeof subscriber.getImgData === 'function'
        ) {
            return subscriber.getImgData();
        }
        return null;
    }

    // onscreenShare
    onscreenShare = (onScreenShare, callback) => {
        this.setState({ screenShare: onScreenShare });
    };

    // The OpenTok widget paints stream.name as its name overlay, and the host
    // publishes as 'Educator'. Relabel it through the subscriber's `name`
    // property. Never hand OTSubscriber a clone of the stream: the SDK reads
    // stream.hasVideo off whatever object it was given, and a clone keeps the
    // value from join time, so a camera that starts after the subscribe stays
    // black until something remounts the tile (e.g. starting a screen share).
    getDisplayProperties = (stream) => ({ name: toDisplayName(stream.name) });

    //exactly ONE render location per stream
    getStreamRenderLocation = (stream) => {
        const userSession = JSON.parse(sessionStorage.getItem('userSession'));
        const isScreenShareStream = (candidateStream) =>
            candidateStream?.videoType === 'screen' || candidateStream?.name === 'screen';
        // Fallback for third-party users: use educatorName from URL params or first stream that's not current user
        const tutorName =
            this.props.studentDetails?.tutor?.name ||
            this.props.urlParams?.educatorName ||
            this.props.streams?.find((s) => s.name !== userSession?.name && !isScreenShareStream(s))
                ?.name;
        const isScreenShared = this.props.streams.some((i) => isScreenShareStream(i));
        const showStudents = this.props.streams.find((e) => e.name !== tutorName);

        if (isScreenShared && isScreenShareStream(stream)) {
            return 'main-area';
        }

        // Handle group session logic first to avoid conflicts
        if (this.props.sessionTypeParamsGroup) {
            // In group sessions, main tutor goes to main area unless screen sharing
            if (!isScreenShared && stream.name === tutorName) {
                return 'main-area';
            }
            // When screen sharing in group session, tutor goes to small overlay
            if (isScreenShared && stream.name === tutorName) {
                return 'carousel';
            }
            // Other participants (non-tutor, non-screen) go to carousel
            if (stream.name !== tutorName && !isScreenShareStream(stream)) {
                return 'carousel';
            }
        } else {
            // Non-group session logic
            if (!isScreenShared && stream.name === tutorName) {
                return 'main-area';
            }
            if (
                isScreenShared &&
                stream.name === tutorName &&
                (this.props.role === Patient || this.props.OwnerAsCollaborator)
            ) {
                return 'carousel';
            }
            if (isScreenShared && this.props.role === Doctor && !isScreenShareStream(stream)) {
                return 'carousel';
            }
        }
        if (
            (this.props.role === 'Patient' ||
                (this.props.OwnerAsCollaborator &&
                    this.props.OwnerAsCollaborator !== userSession.id)) &&
            showStudents &&
            stream.name !== tutorName &&
            !isScreenShareStream(stream)
        ) {
            return 'carousel';
        }
        if (this.props.role === Doctor) {
            return 'doctor-grid';
        }
        return null;
    };

    // Places a stream's video in the current layout. The OTSubscriber itself is
    // rendered by renderSubscriberPortals(), so moving the tile between the
    // carousel, the main area and the grid (screen share start/stop, whiteboard)
    // never remounts it - each remount tore down and rebuilt the audio
    // subscription, which was heard as the speaker's voice breaking up.
    renderSubscriber = (stream) => {
        this.placedStreams.set(stream.id, stream);
        return <SubscriberSlot host={this.subscriberHosts.get(stream.id)} />;
    };

    // Rendered last and always at the same place in the tree, after every
    // renderSubscriber() call of this render.
    renderSubscriberPortals = () =>
        Array.from(this.placedStreams.values()).map((stream) =>
            this.subscriberHosts.portal(
                stream.id,
                <OTSubscriber
                    key={stream.id}
                    session={this.props.sessionHelper.session}
                    stream={stream}
                    properties={this.getDisplayProperties(stream)}
                    eventHandlers={this.subscriberEventHandlers}
                    onGetSubscriber={(sub) => {
                        this.handleGetSubscriber(stream, sub);
                    }}
                    style={{ width: '100%', height: '100%' }}
                />
            )
        );

    render() {
        const { streams } = { ...this.props };
        this.placedStreams = new Map();
        console.log(streams, 'streams');

        const showRightIcon = this.shouldShowRightIcon();
        const showLeftIcon = this.shouldShowLeftIcon();
        const userSession = JSON.parse(sessionStorage.getItem('userSession'));
        const isScreenShareStream = (stream) =>
            stream?.videoType === 'screen' || stream?.name === 'screen';
        // Use same fallback logic for tutorName
        const tutorName =
            this.props.studentDetails?.tutor?.name ||
            this.props.urlParams?.educatorName ||
            streams?.find((s) => s.name !== userSession?.name && !isScreenShareStream(s))?.name;
        const showStudents = streams.find((e) => e.name !== tutorName);
        const isTutorSide =
            this.props.studentDetails?.tutor?.userId ===
            JSON.parse(sessionStorage.getItem('userSession'))?.id;
        const isGroupUserSide = this.props.sessionTypeParamsGroup && !isTutorSide;
        const hasScreenShare = streams && streams.some((stream) => isScreenShareStream(stream));
        const containerWidth = `${100 / Math.ceil(Math.sqrt(streams.length))}%`;
        const containerHeight = `${100 / Math.ceil(streams.length / Math.ceil(Math.sqrt(streams.length)))}%`;

        const shouldShowTopWifi =
            !this.props.sessionTypeParamsGroup &&
            (this.props.role === Doctor || !showStudents) &&
            isTutorSide;
        const shouldShowCarouselWifi =
            isGroupUserSide || !isTutorSide || (isTutorSide && hasScreenShare);
        const shouldShowGroupFallbackWifi = isGroupUserSide && !showStudents;

        return (
            <div className="parent-div">
                {(shouldShowTopWifi && !hasScreenShare) || shouldShowGroupFallbackWifi
                    ? this.props.renderLogoWithWifi()
                    : ''}
                <div
                    className="classroom-video classroom-user"
                    style={{ backgroundColor: 'black' }}
                >
                    {this.props.isWhiteboardOpen && (
                        <div style={{ width: '100%', height: '100%' }}>
                            <ScreenShotEditModal visitId={this.props.visitId} />
                        </div>
                    )}
                    {/* display:contents makes wrapper transparent to CSS layout (no layout break).
                        display:none hides subscribers during whiteboard but keeps them mounted (no audio break). */}
                    <div
                        style={{
                            display: this.props.isWhiteboardOpen ? 'none' : 'contents',
                        }}
                    >
                        <>
                            {(this.props.role === 'Patient' ||
                                this.props.sessionTypeParamsGroup ||
                                (this.props.OwnerAsCollaborator &&
                                    this.props.OwnerAsCollaborator !== userSession.id) ||
                                (this.props.role === Doctor && hasScreenShare)) && (
                                <>
                                    {streams && streams.length && showStudents && (
                                        <div
                                            style={{ height: '20%', paddingTop: '4px' }}
                                            className="w-full"
                                        >
                                            <div className="h-full carousel">
                                                <div className="relative w-full h-full mb-2 overflow-hidden">
                                                    <div className="w-full h-full">
                                                        <div
                                                            ref={this.carousel}
                                                            className={`h-full carousel-container relative flex gap-1 overflow-hidden scroll-smooth snap-x snap-mandatory touch-pan-x z-0 ${!showRightIcon ? 'justify-center' : ''}`}
                                                        >
                                                            {streams.map((stream, index) => {
                                                                const renderLocation =
                                                                    this.getStreamRenderLocation(
                                                                        stream
                                                                    );
                                                                return renderLocation ===
                                                                    'carousel' ? (
                                                                    <React.Fragment key={stream.id}>
                                                                        <div className="relative text-center carousel-item snap-start">
                                                                            <div
                                                                                className={
                                                                                    this.state.raisedHands.has(
                                                                                        stream.id
                                                                                    )
                                                                                        ? 'raisehand-animation'
                                                                                        : ''
                                                                                }
                                                                                style={{
                                                                                    position:
                                                                                        'relative',
                                                                                    width: '264px',
                                                                                    height: '100%',
                                                                                }}
                                                                            >
                                                                                {this.state.raisedHands.has(
                                                                                    stream.id
                                                                                ) && (
                                                                                    <iconify-icon
                                                                                        icon="ic:outline-back-hand"
                                                                                        width="24"
                                                                                        height="24"
                                                                                        style={{
                                                                                            position:
                                                                                                'absolute',
                                                                                            top: '5px',
                                                                                            right: '5px',
                                                                                            zIndex: 99999,
                                                                                            color: '#543cc2',
                                                                                        }}
                                                                                    ></iconify-icon>
                                                                                )}

                                                                                {this.renderSubscriber(
                                                                                    stream
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </React.Fragment>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                        {showLeftIcon && (
                                                            <button
                                                                onClick={this.movePrev}
                                                                style={{
                                                                    display: showLeftIcon
                                                                        ? 'block'
                                                                        : 'none',
                                                                }}
                                                                className="absolute top-0 left-0 z-20 w-10 h-full p-0 m-0 text-center text-white transition-all duration-300 ease-in-out opacity-75 hover:bg-blue-900/75 hover:opacity-100"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="w-20 h-12 -ml-5"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                    strokeWidth={2}
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M15 19l-7-7 7-7"
                                                                    />
                                                                </svg>
                                                                <span className="sr-only">
                                                                    Prev
                                                                </span>
                                                            </button>
                                                        )}
                                                        {showRightIcon && (
                                                            <button
                                                                onClick={this.moveNext}
                                                                style={{
                                                                    display: showRightIcon
                                                                        ? 'block'
                                                                        : 'none',
                                                                }}
                                                                className="absolute top-0 right-0 z-20 w-10 h-full p-0 m-0 text-center text-white transition-all duration-300 ease-in-out opacity-75 hover:bg-blue-900/75 hover:opacity-100"
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="w-20 h-12 -ml-5"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                    strokeWidth={2}
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M9 5l7 7-7 7"
                                                                    />
                                                                </svg>
                                                                <span className="sr-only">
                                                                    Next
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {shouldShowCarouselWifi &&
                                                this.props.renderLogoWithWifi()}
                                        </div>
                                    )}

                                    {/* MAIN AREA SECTION */}
                                    <div
                                        style={{
                                            height:
                                                showStudents &&
                                                hasScreenShare &&
                                                this.props.fullScreenEnabled
                                                    ? '70%'
                                                    : showStudents
                                                      ? '80%'
                                                      : '100%',
                                            marginBottom: hasScreenShare ? '300px' : '4px',
                                        }}
                                        className={`w-full ${this.props.fullScreenEnabled ? 'fullscreen-view-educator' : ''}`}
                                    >
                                        {streams &&
                                            streams.map((stream, index) => {
                                                const renderLocation =
                                                    this.getStreamRenderLocation(stream);
                                                return renderLocation === 'main-area' ? (
                                                    <React.Fragment key={stream.id}>
                                                        {this.renderSubscriber(stream)}
                                                    </React.Fragment>
                                                ) : null;
                                            })}
                                    </div>
                                </>
                            )}

                            {/* DOCTOR GRID SECTION */}
                            {this.props.role === Doctor &&
                                streams &&
                                streams.map((stream, index) => {
                                    const renderLocation = this.getStreamRenderLocation(stream);
                                    console.log('Stream:', stream.id, 'Location:', renderLocation);
                                    return renderLocation === 'doctor-grid' ? (
                                        <div
                                            key={stream.id}
                                            className={
                                                this.state.raisedHands.has(stream.id)
                                                    ? 'raisehand-animation'
                                                    : ''
                                            }
                                            style={{
                                                position: 'relative',
                                                width: containerWidth,
                                                height: containerHeight,
                                            }}
                                        >
                                            {this.state.raisedHands.has(stream.id) && (
                                                <iconify-icon
                                                    icon="ic:outline-back-hand"
                                                    width="24"
                                                    height="24"
                                                    style={{
                                                        position: 'absolute',
                                                        top: '5px',
                                                        right: '5px',
                                                        zIndex:
                                                            (this.props.showMessagePop ||
                                                                this.props.showParticipants ||
                                                                this.props.messageClicked) &&
                                                            this.props.minWidth
                                                                ? '-1'
                                                                : 99999,
                                                        color: '#543cc2',
                                                    }}
                                                ></iconify-icon>
                                            )}
                                            {this.renderSubscriber(stream)}
                                        </div>
                                    ) : null;
                                })}
                        </>
                    </div>
                </div>

                {/* SMALL OVERLAY SECTION */}
                {/* {(this.props.role === Patient ||
                    this.props.OwnerAsCollaborator ||
                    this.props.sessionTypeParamsGroup) &&
                    streams &&
                    streams.map((stream, index) => {
                        const renderLocation = this.getStreamRenderLocation(stream);
                        return renderLocation === 'carousel' ? (
                            <div
                                key={stream.id}
                                className={`${
                                    this.props.fullScreenEnabled ? 'bottom-65' : ''
                                } my-video-screen`}
                                style={{ zIndex: '1' }}
                            >
                                <OTSubscriber
                                    key={stream.id}
                                    session={this.props.sessionHelper.session}
                                    properties={subscriberOptionsSmallVideo}
                                    stream={stream}
                                    eventHandlers={this.subscriberEventHandlers}
                                    onGetSubscriber={(sub) => {
                                        this.handleGetSubscriber(stream, sub);
                                    }}
                                />
                            </div>
                        ) : null;
                    })} */}
                {this.renderSubscriberPortals()}
            </div>
        );
    }
}

export default ClassRoomSubscribers;
